/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Message } from 'primereact/message';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'sharedUi/Dropdown';
import { Toast } from 'sharedUi/Toast';
import { Button } from 'sharedUi/Button';
import PageSkeleton from '../../components/common/page-skeleton';
import { usersService, type GroupedUser } from '../../services/users.service';
import { personnelLookupService } from '../../services/personnel-lookup.service';
import { projectsService, type AccessibleProject } from '../../services/projects.service';
import { assignmentsApi, roleBindingsApi, rolesApi } from '../../services/rbac.service';
import { extractErrorMessage } from '../../utils/error-handler';
import { useCanUpdate, useCanRead, useCanCreate } from '../../hooks/usePermissions';
import { JOB_NAMES } from '../../constants/jobNames';
import PermissionGuard from '../../components/guards/PermissionGuard';
import ScreenHeader from '../../components/common/screen-header';

/** Roles a user holds in ONE project — resolved from that project's account's
 *  assignments (each project account has its own bindings). */
const ProjectRoles = ({ accountId, appId, bindingById, roleById, canRead }: {
  accountId: string; appId: string; bindingById: Map<string, any>; roleById: Map<string, any>; canRead: boolean;
}) => {
  const { data: assignments = [], isFetching } = useQuery({
    queryKey: ['rbac', 'assignments', 'by-user', accountId, appId],
    queryFn: () => assignmentsApi.list({ user_id: accountId, app_id: appId, page: 1, page_size: 200 }).then((r) => r.data || []),
    enabled: canRead && !!accountId,
  });
  if (!canRead) return <span className="text-xs text-ink-muted">—</span>;
  if (isFetching) return <span className="text-xs text-ink-muted">Loading…</span>;
  if (assignments.length === 0) {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-ink-muted italic">
        <i className="pi pi-minus-circle text-[10px] opacity-70" /> No roles yet
      </span>
    );
  }
  const names = Array.from(new Set(assignments.map((a: any) => {
    const b = bindingById.get(a.roleBindingId);
    const role = b ? roleById.get(b.roleId) : undefined;
    return b?.role?.displayName || b?.role?.key || role?.displayName || role?.key || 'Role';
  })));
  return (
    <div className="flex gap-1.5 flex-wrap">
      {names.map((n) => (
        <span key={n} className="inline-flex items-center gap-1 rounded-md bg-indigo-50 dark:bg-indigo-900/30 px-2 py-0.5 text-xs font-medium text-indigo-700 dark:text-indigo-300 ring-1 ring-indigo-100 dark:ring-indigo-800">
          <i className="pi pi-shield text-[10px]" /> {n}
        </span>
      ))}
    </div>
  );
};

// Keep in sync with LANGUAGE_OPTIONS in user-management-form.tsx.
const LANGUAGE_NAMES: Record<string, string> = { '1': 'English', '2': 'Telugu', '3': 'Hindi', '4': 'Urdu' };

const Field = ({ label, value, icon }: { label: string; value: React.ReactNode; icon?: string }) => (
  <div>
    <label className="text-xs font-medium text-ink-muted flex items-center gap-1.5">
      {icon && <i className={`pi ${icon} text-[11px] opacity-60`} />}{label}
    </label>
    <p className="text-sm text-ink mt-1 truncate" title={typeof value === 'string' ? value : undefined}>{value ?? '—'}</p>
  </div>
);

/** A deterministic gradient for the user avatar. */
const avatarGradient = (key: string) => {
  const grads = [
    'linear-gradient(135deg, #6366f1, #4338ca)',
    'linear-gradient(135deg, #0ea5e9, #0369a1)',
    'linear-gradient(135deg, #10b981, #047857)',
    'linear-gradient(135deg, #f59e0b, #b45309)',
    'linear-gradient(135deg, #ec4899, #be185d)',
    'linear-gradient(135deg, #8b5cf6, #6d28d9)',
  ];
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) >>> 0;
  return grads[h % grads.length];
};

/** A soft pill status badge with a status dot — the Pending dot gently pulses. */
const projectStatus = (p: GroupedUser['projects'][number]) => {
  const s = p.blocked
    ? { label: 'Blocked', cls: 'bg-rose-50 text-rose-700 ring-rose-200 dark:bg-rose-900/20 dark:text-rose-300 dark:ring-rose-800', dot: 'bg-rose-500', pulse: false }
    : p.isRegistered
      ? { label: 'Registered', cls: 'bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-300 dark:ring-emerald-800', dot: 'bg-emerald-500', pulse: false }
      : { label: 'Pending', cls: 'bg-amber-50 text-amber-700 ring-amber-200 dark:bg-amber-900/20 dark:text-amber-300 dark:ring-amber-800', dot: 'bg-amber-500', pulse: true };
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ${s.cls}`}>
      <span className="relative flex h-1.5 w-1.5">
        {s.pulse && <span className={`absolute inline-flex h-full w-full rounded-full opacity-60 animate-ping ${s.dot}`} />}
        <span className={`relative inline-flex h-1.5 w-1.5 rounded-full ${s.dot}`} />
      </span>
      {s.label}
    </span>
  );
};

const UserManagementGroupView = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useRef<Toast>(null);
  const canUpdate = useCanUpdate(JOB_NAMES.USER_MANAGEMENT);
  const canReadRoles = useCanRead(JOB_NAMES.USER_ROLE_PERMISSIONS);
  const canManageRoles = useCanCreate(JOB_NAMES.USER_ROLE_PERMISSIONS);

  const [group, setGroup] = useState<GroupedUser | null>(null);
  const [personnelName, setPersonnelName] = useState('');
  const [projects, setProjects] = useState<AccessibleProject[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // Which project we're assigning a role to (inline dialog), + the picked binding.
  const [assignFor, setAssignFor] = useState<{ accountId: string; appId: string; projectName: string } | null>(null);
  const [selectedBindingId, setSelectedBindingId] = useState('');

  // Shared reference lists to resolve each project's role names (cached; also used
  // by the RBAC tabs under the same keys).
  const { data: bindings = [] } = useQuery({ queryKey: ['rbac', 'role-bindings', 'all'], queryFn: () => roleBindingsApi.listAll(), enabled: canReadRoles });
  const { data: roles = [] } = useQuery({ queryKey: ['rbac', 'roles', 'all'], queryFn: () => rolesApi.listAll(), enabled: canReadRoles });
  const bindingById = useMemo(() => new Map(bindings.map((b: any) => [b._id, b])), [bindings]);
  const roleById = useMemo(() => new Map(roles.map((r: any) => [r._id, r])), [roles]);

  // Role bindings the caller can grant IN the project being assigned (that project's
  // own bindings + global ones).
  const bindingOptions = useMemo(() => {
    if (!assignFor) return [];
    return bindings
      // Only THIS project's own bindings (not global or other projects').
      .filter((b: any) => b.appId === assignFor.appId)
      .map((b: any) => {
        const role = b.role?.displayName || b.role?.key || roleById.get(b.roleId)?.displayName || roleById.get(b.roleId)?.key || b.roleId;
        const unit = b.unit?.name || (b.unitId ? b.unitId : 'Whole project');
        return { label: `${role} · ${unit}`, value: b._id };
      });
  }, [bindings, roleById, assignFor]);

  const assignMut = useMutation({
    mutationFn: (v: { accountId: string; roleBindingId: string }) =>
      assignmentsApi.create({ userId: v.accountId, roleBindingId: v.roleBindingId } as any),
    onSuccess: (_d, v) => {
      toast.current?.show({ severity: 'success', summary: 'Role assigned', life: 2500 });
      queryClient.invalidateQueries({ queryKey: ['rbac', 'assignments', 'by-user', v.accountId] });
      setAssignFor(null);
      setSelectedBindingId('');
    },
    onError: (e) => toast.current?.show({ severity: 'error', summary: 'Error', detail: extractErrorMessage(e, 'Failed to assign role'), life: 6000 }),
  });

  const projName = (appId: string) => {
    const p = projects.find((x) => x._id === appId);
    return p ? (p.appCode ? `${p.name} (${p.appCode})` : p.name) : appId;
  };

  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        setLoading(true);
        const [g, accessible] = await Promise.all([
          usersService.getGroupDetail(id),
          projectsService.getAccessible().catch(() => []),
        ]);
        setGroup(g);
        setProjects(accessible);
        if (g.personnelMasterId) {
          const p = await personnelLookupService.getById(g.personnelMasterId).catch(() => null);
          setPersonnelName(p?.name || '');
        }
      } catch (err: any) {
        setError(extractErrorMessage(err, 'Failed to fetch user'));
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  if (loading) return <PageSkeleton />;
  if (error || !group) {
    return (
      <div className="p-4">
        <Message severity="error" text={error || 'User not found'} className="mb-4 w-full" />
        <Button label="Back to Users" icon="pi pi-arrow-left" severity="secondary" outlined onClick={() => navigate('/user-management')} />
      </div>
    );
  }

  const displayName = personnelName || group.email || group.userId || 'User';

  return (
    <PermissionGuard jobName={JOB_NAMES.USER_MANAGEMENT}>
      <Toast ref={toast} position="top-right" />
      <div className="p-3" data-testid="SCR-UserManagement-GroupView">
        <ScreenHeader icon="pi pi-users" title="User Details" onBack={() => navigate('/user-management')} />

        {/* Identity header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <div className="flex items-center gap-3.5">
            <div
              className="w-14 h-14 rounded-2xl flex items-center justify-center text-white text-xl font-bold shadow-sm ring-2 ring-white dark:ring-gray-800 shrink-0"
              style={{ background: avatarGradient(group.key || group.userId || '') }}
            >
              {(personnelName || group.userId || '?').charAt(0).toUpperCase()}
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="text-xl font-bold text-ink truncate">{group.userId || displayName}</h2>
                <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 dark:bg-blue-900/20 px-2 py-0.5 text-xs font-medium text-blue-600 dark:text-blue-300">
                  <i className="pi pi-folder text-[10px]" /> {group.projectCount} {group.projectCount === 1 ? 'project' : 'projects'}
                </span>
              </div>
              <p className="text-sm text-ink-muted mt-0.5 truncate">
                {group.email || (group.personnelMasterId && personnelName) || '—'}
              </p>
            </div>
          </div>
          {canUpdate && id && (
            <Button label="Edit" icon="pi pi-pencil" size="small" onClick={() => navigate(`/user-management/group/${id}/edit`)} />
          )}
        </div>

        {/* Shared identity — same across every project. */}
        <div className="ds-card p-4 mb-3">
          <h2 className="text-sm font-semibold text-ink mb-3 flex items-center gap-2">
            <i className="pi pi-id-card text-blue-600 text-xs" /> Identity <span className="text-ink-muted font-normal">(shared across projects)</span>
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-3">
            <Field icon="pi-user" label="Username" value={group.userId || '—'} />
            {group.personnelMasterId && <Field icon="pi-id-card" label="Personnel" value={personnelName || '—'} />}
            <Field icon="pi-envelope" label="Email" value={group.email || '—'} />
            <Field icon="pi-phone" label="Phone" value={group.phoneNo || '—'} />
            <Field icon="pi-globe" label="Default Language" value={group.defaultlanguageId != null ? (LANGUAGE_NAMES[String(group.defaultlanguageId)] || `#${group.defaultlanguageId}`) : '—'} />
          </div>
        </div>

        {/* Per-project accounts — project, roles held there, status, and an assign action. */}
        <div className="ds-card overflow-hidden">
          <h2 className="text-sm font-semibold text-ink px-4 py-2.5 border-b border-line">
            Projects <span className="text-ink-muted font-normal">({group.projectCount})</span>
          </h2>
          <div className="hidden sm:flex items-center gap-4 px-4 py-2 bg-gray-50 dark:bg-gray-800/40 text-[11px] font-semibold uppercase tracking-wide text-ink-muted border-b border-line">
            <span className="w-52 shrink-0">Project</span>
            <span className="flex-1 min-w-0">Roles in this project</span>
            <span className="w-24 shrink-0 text-center">Status</span>
            <span className="w-32 shrink-0 text-right">Access</span>
          </div>
          <div className="flex flex-col divide-y divide-gray-100 dark:divide-gray-800">
            {group.projects.map((p) => {
              const proj = projects.find((x) => x._id === p.appId);
              return (
                <div key={p._id} className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 px-4 py-3 hover:bg-gray-50/70 dark:hover:bg-gray-800/30 transition-colors">
                  {/* Project — a small color dot + clean name/code, so rows align neatly. */}
                  <div className="w-52 shrink-0 flex items-center gap-2 min-w-0">
                    <span className="w-2 h-2 rounded-full bg-blue-500 shrink-0" />
                    <span className="text-sm font-semibold text-ink truncate">{proj?.name || p.appId}</span>
                    {proj?.appCode && (
                      <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-800 text-ink-muted shrink-0">{proj.appCode}</span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <ProjectRoles accountId={p._id} appId={p.appId} bindingById={bindingById} roleById={roleById} canRead={canReadRoles} />
                  </div>
                  <div className="sm:w-24 shrink-0 flex gap-1 flex-wrap sm:justify-center">
                    {projectStatus(p)}
                  </div>
                  <div className="sm:w-32 shrink-0 flex sm:justify-end">
                    {canManageRoles && (
                      <button
                        type="button"
                        onClick={() => { setSelectedBindingId(''); setAssignFor({ accountId: p._id, appId: p.appId, projectName: projName(p.appId) }); }}
                        className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-blue-200 dark:border-blue-800 bg-blue-50/60 dark:bg-blue-900/20 px-2.5 py-1.5 text-xs font-medium text-blue-600 dark:text-blue-300 hover:bg-blue-600 hover:border-blue-600 hover:text-white transition-colors"
                      >
                        <i className="pi pi-plus text-[10px]" /> Assign role
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Inline assign-role dialog — grants an existing role binding to this
            project's account, without leaving the screen. */}
        <Dialog
          visible={!!assignFor}
          onHide={() => setAssignFor(null)}
          header={`Assign role — ${assignFor?.projectName ?? ''}`}
          style={{ width: '440px' }}
          modal
          dismissableMask
        >
          <div className="flex flex-col gap-3">
            <label className="text-sm font-medium text-ink">Role binding</label>
            <Dropdown
              value={selectedBindingId}
              options={bindingOptions}
              onChange={(e: any) => setSelectedBindingId(e.value)}
              placeholder={bindingOptions.length ? 'Select a role binding' : 'No role bindings for this project'}
              filter
              className="w-full"
            />
            <p className="text-xs text-ink-muted">
              Grants this role to the user in <strong>{assignFor?.projectName}</strong>. Manage bindings in Access Control → Role Bindings.
            </p>
            <div className="flex justify-end gap-2 mt-1">
              <Button label="Cancel" severity="secondary" outlined onClick={() => setAssignFor(null)} disabled={assignMut.isPending} />
              <Button
                label={assignMut.isPending ? 'Assigning…' : 'Assign'}
                icon={assignMut.isPending ? 'pi pi-spin pi-spinner' : 'pi pi-check'}
                disabled={!selectedBindingId || assignMut.isPending}
                onClick={() => assignFor && assignMut.mutate({ accountId: assignFor.accountId, roleBindingId: selectedBindingId })}
              />
            </div>
          </div>
        </Dialog>
      </div>
    </PermissionGuard>
  );
};

export default UserManagementGroupView;
